<script setup lang="ts">
import { computed, ref } from 'vue'

import { TextInput } from '@collector/shared-ui'
import type { SubParticipant } from '@collector/sportsapi-types'

import type { LineupConfig } from '../../../../interfaces/LineupConfig'
import type { LineupModel } from '../../../../interfaces/LineupModel'
import AddToPositionButtonGroup from '../../../common/AddToPositionButtonGroup.vue'
import ShirtNrTextInput from '../../../common/ShirtNrTextInput.vue'

type Props = {
  config: LineupConfig
  lineup: LineupModel
}
const props = defineProps<Props>()

type Emits = {
  add: [positionKey: PropertyKey, player: Partial<SubParticipant>]
}
const emit = defineEmits<Emits>()

// state
const shirtNr = ref('')
const playerShortName = ref('')

// computed
const buttonsDisabled = computed(() => {
  const shortNameTrimmed = playerShortName.value.trim()
  if (shortNameTrimmed === '') return { disabled: true }
  else {
    const shortNameExists = Object.values(props.lineup)
      .flat()
      .some((p) => p.short_name === shortNameTrimmed)
    if (shortNameExists)
      return {
        disabled: true,
        reasonText: `Player with name "${shortNameTrimmed}" is already added`,
      }
  }
  return { disabled: false }
})

// methods
async function handleAdd(positionKey: PropertyKey): Promise<void> {
  const shortNameTrimmed = playerShortName.value.trim()
  const participant: Partial<SubParticipant> = {
    short_name: shortNameTrimmed,
    shirt_nr: shirtNr.value,
  }
  emit('add', positionKey, participant)
  clear()
}

function clear(): void {
  shirtNr.value = ''
  playerShortName.value = ''
}
</script>

<template>
  <div class="bg-neutral-light-7 flex gap-1 rounded-md p-2">
    <ShirtNrTextInput v-model="shirtNr" />
    <div class="w-full">
      <TextInput
        v-model="playerShortName"
        placeholder="Player name"
        :maxlength="20"
        width="full"
        inputClass="px-2"
      />
      <div class="text-2xs text-neutral-dark-0 mt-0.5">Max 20 characters</div>
    </div>
    <AddToPositionButtonGroup
      type="new"
      :config
      :lineup
      :shirtNr
      :disabled="buttonsDisabled.disabled"
      :disabledReasonText="buttonsDisabled.reasonText"
      @add="handleAdd"
    />
  </div>
</template>
